import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import Button from './Button';

const PollModal = ({ isOpen, onClose, onSubmit, poll = null }) => {
  const [question, setQuestion] = useState(poll?.question || '');
  const [options, setOptions] = useState(poll?.options || ['', '']);
  const [templateId, setTemplateId] = useState(poll?.template_id || 1);

  // Update state when poll prop changes
  useEffect(() => {
    if (poll) {
      setQuestion(poll.question || '');
      setOptions(poll.options || ['', '']);
      setTemplateId(poll.template_id || 1);
    } else {
      setQuestion('');
      setOptions(['', '']);
      setTemplateId(1);
    }
  }, [poll]);

  const handleAddOption = () => {
    if (options.length < 6) { // Limit to 6 options
      setOptions([...options, '']);
    }
  };

  const handleRemoveOption = (index) => {
    if (options.length > 2) { // Keep at least 2 options
      const newOptions = options.filter((_, i) => i !== index);
      setOptions(newOptions);
    }
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!question.trim()) {
      alert('Please enter a poll question');
      return;
    }
    
    const validOptions = options.filter(option => option.trim());
    if (validOptions.length < 2) {
      alert('Please provide at least 2 options');
      return;
    }
    
    onSubmit({
      question: question.trim(),
      options: validOptions,
      template_id: templateId
    });
  };

  const handleClose = () => {
    setQuestion(poll?.question || '');
    setOptions(poll?.options || ['', '']);
    setTemplateId(poll?.template_id || 1);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={poll ? "Edit Poll" : "Create Poll"}>
      <form onSubmit={handleSubmit} className="space-y-6 p-4">
        <div>
          <label htmlFor="pollQuestion" className="block text-sm font-medium text-gray-900 mb-2">
            Poll Question <span className="text-red-500">*</span>
          </label>
          <textarea
            id="pollQuestion"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={3}
            placeholder="Enter your poll question..."
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-gray-900 placeholder-gray-500 resize-none"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-900 mb-2">
            Poll Options <span className="text-red-500">*</span>
          </label>
          <div className="space-y-3">
            {options.map((option, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="text"
                  value={option}
                  onChange={(e) => handleOptionChange(index, e.target.value)}
                  placeholder={`Option ${index + 1}`}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-gray-900 placeholder-gray-500"
                  required
                />
                {options.length > 2 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveOption(index)}
                    className="px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
          
          {options.length < 6 && (
            <button
              type="button"
              onClick={handleAddOption}
              className="mt-3 px-4 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span>Add Option</span>
            </button>
          )}
        </div>

        <div>
          <label htmlFor="templateSelect" className="block text-sm font-medium text-gray-900 mb-2">
            Display Template
          </label>
          <select
            id="templateSelect"
            value={templateId}
            onChange={(e) => setTemplateId(parseInt(e.target.value))}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-gray-900"
          >
            <option value={1}>Lower Third</option>
            <option value={2}>Full Screen</option>
          </select>
        </div>

        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
          >
            {poll ? 'Update Poll' : 'Create Poll'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default PollModal;
